import axios, { type AxiosProgressEvent } from 'axios';

import { type Common } from './common.js';
import type * as ExternalDocumentPacket from './models/externalDocumentPacket.js';
import { type PaginatedQueryOptions } from './models/pagination/PaginatedQueryOptions.type.js';
import { type PaginationResponse } from './models/pagination/PaginationResponse.type.js';
import type * as Workflow from './models/workflow.js';

export class API {
  common: Common;
  baseEndpoint: string;

  constructor(common: Common) {
    this.common = common;
    this.baseEndpoint = '/external-document-packets/';
  }

  async create(
    toAdd: ExternalDocumentPacket.CreateValues & {
      copyToLabelboxBucket: boolean;
    }
  ): Promise<ExternalDocumentPacket.ExternalDocumentPacket> {
    const url = this.common.constructPath(this.baseEndpoint);
    const packet = await this.common.postJSON(url, toAdd);
    return packet;
  }

  async findOneById(
    id: string,
    showHistory: boolean = false
  ): Promise<{
    packet: ExternalDocumentPacket.ExternalDocumentPacket;
    workflows: Workflow.Workflow[];
  }> {
    const url = this.common.constructPath(`${this.baseEndpoint}${id}?showHistory=${showHistory}`);
    return await this.common.getJSON(url);
  }

  async findAllPaginated(
    filters: PaginatedQueryOptions<ExternalDocumentPacket.FindAllOptions>
  ): Promise<PaginationResponse<ExternalDocumentPacket.ExternalDocumentPacketWithWorkflow>> {
    const url = this.common.constructPath(`${this.baseEndpoint}`, filters);
    return await this.common.getJSON(url);
  }

  async findAllByOrganizationId(
    organizationId: string
  ): Promise<ExternalDocumentPacket.ExternalDocumentPacketWithWorkflow[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}organization/${organizationId}`);
    return await this.common.getJSON(url);
  }

  async findAllSources(): Promise<string[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/sources`);
    return await this.common.getJSON(url);
  }

  async classify(id: string): Promise<void> {
    const url = this.common.constructPath(`${this.baseEndpoint}${id}/classify`);
    await this.common.postJSON(url, {});
  }

  async copyPacketToSftp({
    id,
    returnReason,
  }: {
    id: string;
    isTest?: boolean;
    returnReason?: string;
  }): Promise<{ success: boolean; error?: string }> {
    const url = this.common.constructPath(`${this.baseEndpoint}${id}/copy-sftp`);
    return await this.common.postJSON(url, { returnReason });
  }

  async updatePacketWorkflow(
    id: string,
    status: Workflow.ExternalDocumentPacket.Status,
    reasonsForReview?: string[]
  ): Promise<Workflow.WorkflowV2<typeof Workflow.ExternalDocumentPacket.Kind>> {
    const url = this.common.constructPath(`${this.baseEndpoint}workflow/packet/${id}`);
    return await this.common.patchJSON(url, { status, reasonsForReview });
  }

  async updateOrderWorkflow({
    id,
    orderPacketId,
    status,
  }: {
    id: string;
    orderPacketId?: string;
    status: Workflow.ExternalOrderPacket.Status;
  }): Promise<Workflow.WorkflowV2<typeof Workflow.ExternalOrderPacket.Kind>> {
    const url = this.common.constructPath(`${this.baseEndpoint}workflow/order/${id}`);
    return await this.common.patchJSON(url, { status, orderPacketId });
  }

  async getTestUploadUrl(fileName: string): Promise<{ url: string; gsPath: string }> {
    const url = this.common.constructPath(`${this.baseEndpoint}test-upload-url`, {
      fileName,
    });
    return await this.common.getJSON(url);
  }

  async upload(args: {
    file: File;
    url: string;
    contentType: string;
    progressHandler?: (event: AxiosProgressEvent) => void;
  }): Promise<number> {
    const { file, url, contentType, progressHandler } = args;
    const res = await axios.put(url, file, {
      onUploadProgress: progressHandler,
      headers: { 'Content-Type': contentType },
    });
    return res.status;
  }
}
